/**
 * BucketGrid (PHASE1 §7.2). The tap-to-categorize grid: every non-archived
 * bucket in canonical order as a compact `BucketChip`, visually sectioned
 * under its bucket-group heading, the first nine carrying a `1–9` keyboard
 * badge. When the item has a suggestion it is highlighted with an
 * "Akzeptieren" (Enter) affordance; a "Aufteilen" button opens the SplitEditor.
 *
 * The chip order is exactly the `buckets` prop (`useBuckets()`: group then
 * bucket sort_order), so the numeric hotkeys in `InboxPage` line up
 * deterministically with the chips — the sections merely chunk that flat list
 * by `group_id`; they never reorder it.
 */
import { useMemo } from 'react';
import { Scissors, Sparkles } from 'lucide-react';
import { BucketChip } from '../../components/ui/BucketChip.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { useBucketMap, useBucketTree } from '../../lib/hooks/useBuckets.ts';
import type { BucketOut, BucketSuggestion } from '../../lib/types.ts';

const SUGGESTION_SOURCE: Record<BucketSuggestion['source'], string> = {
  rule: 'Regel',
  history: 'Verlauf',
};

interface GridSection {
  groupId: number;
  buckets: { bucket: BucketOut; index: number }[];
}

export interface BucketGridProps {
  buckets: BucketOut[];
  suggestion: BucketSuggestion | null;
  onAssign: (bucketId: number) => void;
  onAccept: () => void;
  onSplit: () => void;
}

export function BucketGrid({ buckets, suggestion, onAssign, onAccept, onSplit }: BucketGridProps) {
  const bucketMap = useBucketMap();
  const groups = useBucketTree();
  const suggested = suggestion ? bucketMap.get(suggestion.bucket_id) : undefined;

  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
  const sections = useMemo<GridSection[]>(() => {
    const out: GridSection[] = [];
    buckets.forEach((bucket, index) => {
      const last = out[out.length - 1];
      if (last && last.groupId === bucket.group_id) {
        last.buckets.push({ bucket, index });
      } else {
        out.push({ groupId: bucket.group_id, buckets: [{ bucket, index }] });
      }
    });
    return out;
  }, [buckets]);

  return (
    <div className="space-y-4">
      {suggested ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-sky-50 px-4 py-3 dark:bg-sky-950">
          <div className="flex min-w-0 items-center gap-2">
            <Sparkles className="h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden />
            <span className="text-sm text-sky-900 dark:text-sky-100">
              Vorschlag ({SUGGESTION_SOURCE[suggestion?.source ?? 'history']}):
            </span>
            <BucketChip bucket={suggested} size="sm" />
          </div>
          <Button size="sm" onClick={onAccept}>
            Akzeptieren
            <kbd className="rounded bg-white/25 px-1 text-[10px]">Enter</kbd>
          </Button>
        </div>
      ) : null}

      <div className="space-y-3">
        {sections.map((section) => {
          const group = groupById.get(section.groupId);
          return (
            <section key={section.groupId} className="space-y-1.5">
              {group ? (
                <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {group.icon ? <span aria-hidden>{group.icon}</span> : null}
                  {group.name}
                </h3>
              ) : null}
              <div className="flex flex-wrap gap-1.5">
                {section.buckets.map(({ bucket, index }) => (
                  <BucketChip
                    key={bucket.id}
                    bucket={bucket}
                    size="md"
                    index={index}
                    selected={bucket.id === suggestion?.bucket_id}
                    onClick={() => onAssign(bucket.id)}
                    className="min-h-9 max-w-full"
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <div className="flex justify-center">
        <Button variant="secondary" size="sm" onClick={onSplit}>
          <Scissors className="h-4 w-4" aria-hidden />
          Aufteilen …
        </Button>
      </div>
    </div>
  );
}
